import { slackEmojiToUnicode } from './emojiMapper';

export const formatSlackMessage = (text, users) => {
  if (!text) return '';

  // First, normalize any pre-escaped HTML entities back to their original form
  // This handles cases where text comes in already escaped
  let normalizedText = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

  // Now escape HTML to prevent XSS
  let formattedText = normalizedText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Replace user mentions <@U123456> with styled spans (unescape first)
  formattedText = formattedText.replace(/&lt;@([A-Z0-9]+)&gt;/g, (match, userId) => {
    const user = users[userId];
    if (user) {
      const userName = user.real_name || user.name || userId;
      return `<span class="mention" data-user-id="${userId}">@${userName}</span>`;
    }
    return match;
  });

  // Replace channel mentions
  formattedText = formattedText.replace(/&lt;#([A-Z0-9]+)\|?([^&]*)&gt;/g, (match, channelId, channelName) => {
    return `<span class="channel-mention">#${channelName || channelId}</span>`;
  });

  // Format links
  formattedText = formattedText.replace(/&lt;(https?:\/\/[^|&]+)\|?([^&]*)&gt;/g, (match, url, linkText) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText || url}</a>`;
  });

  // Auto-link plain URLs (not already in < >)
  formattedText = formattedText.replace(/(^|[^"'])(https?:\/\/[^\s<>"]+)/g, (match, prefix, url) => {
    return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });

  // Format mailto links
  formattedText = formattedText.replace(/&lt;mailto:([^|&]+)\|?([^&]*)&gt;/g, (match, email, linkText) => {
    return `<a href="mailto:${email}">${linkText || email}</a>`;
  });

  // Format code blocks (```)
  formattedText = formattedText.replace(/```([^`]+)```/g, (match, code) => {
    return `<pre><code>${code}</code></pre>`;
  });

  // Format inline code (`)
  formattedText = formattedText.replace(/`([^`]+)`/g, (match, code) => {
    return `<code class="inline-code">${code}</code>`;
  });

  // Format bold (*text*)
  formattedText = formattedText.replace(/\*([^*]+)\*/g, (match, text) => {
    return `<strong>${text}</strong>`;
  });

  // Format italic (_text_)
  formattedText = formattedText.replace(/_([^_]+)_/g, (match, text) => {
    return `<em>${text}</em>`;
  });

  // Format strikethrough (~text~)
  formattedText = formattedText.replace(/~([^~]+)~/g, (match, text) => {
    return `<del>${text}</del>`;
  });

  // Format lists and blockquotes (convert bullet points and > quotes to HTML)
  // Split by lines and process
  const lines = formattedText.split('\n');
  const processedLines = [];
  let inList = false;
  let listType = null;
  let listItems = [];
  let inBlockquote = false;
  let blockquoteLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^•\s+(.+)$/);
    const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    const quoteMatch = line.match(/^&gt;\s?(.*)$/);

    // Handle blockquotes
    if (quoteMatch) {
      // Close any open list first
      if (inList) {
        processedLines.push(`<${listType}>${listItems.join('')}</${listType}>`);
        listItems = [];
        inList = false;
        listType = null;
      }
      inBlockquote = true;
      blockquoteLines.push(quoteMatch[1] || '');
    } else {
      // Close blockquote if we were in one
      if (inBlockquote) {
        processedLines.push(`<blockquote class="slack-quote">${blockquoteLines.join('<br>')}</blockquote>`);
        blockquoteLines = [];
        inBlockquote = false;
      }

      if (bulletMatch) {
        if (!inList || listType !== 'ul') {
          // Close previous list if exists
          if (inList) {
            processedLines.push(`<${listType}>${listItems.join('')}</${listType}>`);
            listItems = [];
          }
          inList = true;
          listType = 'ul';
        }
        listItems.push(`<li>${bulletMatch[1]}</li>`);
      } else if (numberedMatch) {
        if (!inList || listType !== 'ol') {
          // Close previous list if exists
          if (inList) {
            processedLines.push(`<${listType}>${listItems.join('')}</${listType}>`);
            listItems = [];
          }
          inList = true;
          listType = 'ol';
        }
        listItems.push(`<li>${numberedMatch[2]}</li>`);
      } else {
        if (inList) {
          // Close current list and output as single element
          processedLines.push(`<${listType}>${listItems.join('')}</${listType}>`);
          listItems = [];
          inList = false;
          listType = null;
        }
        // Only add non-empty lines
        if (line.trim()) {
          processedLines.push(line);
        }
      }
    }
  }

  // Close any remaining list or blockquote
  if (inBlockquote && blockquoteLines.length > 0) {
    processedLines.push(`<blockquote class="slack-quote">${blockquoteLines.join('<br>')}</blockquote>`);
  }
  if (inList && listItems.length > 0) {
    processedLines.push(`<${listType}>${listItems.join('')}</${listType}>`);
  }

  // Join processed lines, but don't add <br> after block elements (lists, blockquotes)
  // Block elements don't need extra line breaks
  formattedText = '';
  for (let i = 0; i < processedLines.length; i++) {
    const line = processedLines[i];
    const isBlock = line.startsWith('<ul>') || line.startsWith('<ol>') || line.startsWith('<blockquote');
    const prevIsBlock = i > 0 && (processedLines[i - 1].endsWith('</ul>') || processedLines[i - 1].endsWith('</ol>') || processedLines[i - 1].endsWith('</blockquote>'));

    if (i > 0 && !prevIsBlock && !isBlock) {
      formattedText += '<br>';
    }
    formattedText += line;
  }

  // Convert Slack emoji codes :emoji_name: to Unicode emojis
  formattedText = formattedText.replace(/:([a-z0-9_\-]+):/gi, (match, emojiName) => {
    const emoji = slackEmojiToUnicode(emojiName);
    if (emoji === `:${emojiName}:`) {
      return match;
    }
    return emoji;
  });

  // Convert newlines to <br>
  formattedText = formattedText.replace(/\n/g, '<br>');

  return formattedText;
};

// Convert plain text mentions to Slack format
export const processTextForMentions = (text, users) => {
  let processedText = text;
  const mentions = [];

  // Find @mentions in the text
  const mentionRegex = /@(\w+)/g;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const username = match[1];
    // Find the user by name
    const user = Object.values(users).find(u =>
      u.name === username ||
      (u.real_name && u.real_name.toLowerCase().includes(username.toLowerCase()))
    );

    if (user) {
      mentions.push({ username, userId: user.id });
    }
  }

  return { text: processedText, mentions };
};

// Convert emoji codes in plain text (for incoming messages)
export const convertEmojiCodes = (text) => {
  if (!text) return '';

  // Convert Slack emoji codes :emoji_name: to Unicode emojis
  return text.replace(/:([a-z0-9_\-]+):/gi, (match, emojiName) => {
    const emoji = slackEmojiToUnicode(emojiName);
    // If we got back the same thing with colons, it wasn't found, so return original
    if (emoji === `:${emojiName}:`) {
      return match;
    }
    return emoji;
  });
};